import { useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import type { ExploreSearchParams } from "@/routes/explore/index";

import { RadioIcon } from "./icons";

interface RadioFilterProps extends React.ComponentProps<"input"> {
  label?: React.ReactNode;
  options: string[] | string[][] | [string, React.ReactNode][];
  selected?: string;
}

export default function RadioFilter({
  options,
  selected,
  label,
  ...props
}: RadioFilterProps) {
  const [value, setValue] = useState<string | undefined>();

  const search = useSearch({ strict: false }) as ExploreSearchParams;

  useEffect(() => {
    if (selected !== undefined) {
      setValue(selected);
      return;
    }

    const fieldValue = props.name
      ? search[props.name as keyof ExploreSearchParams]
      : undefined;

    if (!fieldValue) {
      setValue(undefined);
      return;
    }

    setValue(String(fieldValue));
  }, [search, props.name, selected]);

  return (
    <>
      {!!label && (
        <h4 className="mb-5 font-medium text-16 leading-[1.5em] md:mb-2.5 md:leading-[1.2em]">
          {label}
        </h4>
      )}
      <div className="flex flex-col items-stretch gap-5 md:gap-2.5">
        {options.map((option) => {
          const [key, l] =
            typeof option === "string" ? [option, option] : option;

          return (
            <Label
              className="flex cursor-pointer items-center justify-between md:flex-row-reverse md:justify-end"
              key={key}
            >
              <Input
                checked={value === key}
                className="hidden"
                name={props.name}
                onChange={() => setValue(key)}
                type="radio"
                value={key}
              />
              <span className="text-14/[1.2em]">{l}</span>
              <span
                className={cn(
                  "relative block before:block before:size-5 before:rounded-full before:border before:border-white/20 before:transition-opacity before:content-[''] md:mr-2",
                  value === key && "before:opacity-0"
                )}
              >
                <RadioIcon
                  className={cn(
                    "absolute top-0 left-0 opacity-0 transition-opacity",
                    value === key && "opacity-100"
                  )}
                />
              </span>
            </Label>
          );
        })}
      </div>
    </>
  );
}
